import React, { useState } from 'react';
import { Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import { Menu, ChevronDown, User, LogOut, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Toaster } from '../ui/sonner';
import NotificationDropdown from '../NotificationDropdown';
import ThemeToggle from '../ui/ThemeToggle';
import ChatSlideout from '../ChatSlideout';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const DashboardLayout = () => {
  const { user, loading, logout, isImpersonating, stopImpersonation } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
    }
  };

  return (
    <div className="dashboard-layout" data-testid="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <main className="main-content">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="open-mobile-menu"
            >
              <Menu size={24} />
            </Button>
            
            <div className="flex-1" />
            
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Impersonation indicator */}
              {isImpersonating && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStopImpersonation}
                  className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 mr-2"
                  data-testid="stop-impersonation-header-btn"
                >
                  Return to Super Admin
                </Button>
              )}

              {/* Notifications */}
              <NotificationDropdown />

              {/* Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 px-2 hover:bg-slate-100"
                    data-testid="profile-dropdown-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center overflow-hidden">
                      {user?.avatar_url ? (
                        <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs font-medium text-white">
                          {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      )}
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                    <ChevronDown size={16} className="text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 sm:hidden">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="sm:hidden" />
                  <DropdownMenuItem 
                    onClick={() => navigate('/profile')}
                    data-testid="profile-menu-item"
                  >
                    <User size={16} className="mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/settings')}
                    data-testid="settings-menu-item"
                  >
                    <Settings size={16} className="mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600"
                    data-testid="logout-menu-item"
                  >
                    <LogOut size={16} className="mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      <Toaster position="top-right" richColors />
      
      {/* Team Chat Slideout - WebSocket now only connects when chat is opened */}
      <ChatSlideout />
    </div>
  );
};

export default DashboardLayout;
